import { useAuthStore } from '../store/auth';
import { useCallback } from 'react';
import { authApi } from '../services/api';

export const useAuth = () => {
  const { user, token, isAuthenticated, isLoading, setUser, setToken, setLoading, setError, logout } =
    useAuthStore();

  const login = useCallback(
    async (username: string, password: string) => {
      try {
        setLoading(true);
        setError(null);

        const response = await authApi.login(username, password);
        const { access_token, role, team_id } = response.data;

        const user = { username, role, team_id };

        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));

        setToken(access_token);
        setUser(user);

        return true;
      } catch (err: any) {
        const errorMsg = err.response?.data?.detail || 'Login failed';
        setError(errorMsg);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [setToken, setUser, setLoading, setError]
  );

  const signup = useCallback(
    async (username: string, password: string, inviteCode: string) => {
      try {
        setLoading(true);
        setError(null);

        const response = await authApi.signup(username, password, inviteCode);
        const { access_token, role, team_id } = response.data;

        const user = { username, role, team_id };

        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));

        setToken(access_token);
        setUser(user);

        return true;
      } catch (err: any) {
        const errorMsg = err.response?.data?.detail || 'Account creation failed';
        setError(errorMsg);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [setToken, setUser, setLoading, setError]
  );

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const canScout = isAuthenticated && (user?.role === 'Admin' || user?.role === 'Scout');
  const canViewAnalytics = isAuthenticated &&
    (user?.role === 'Admin' || user?.role === 'Strategist' || user?.role === 'Scout');
  const isAdmin = isAuthenticated && user?.role === 'Admin';

  return {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    signup,
    logout: handleLogout,
    canScout,
    canViewAnalytics,
    isAdmin,
  };
};
